/**
 * @file This file contains the main Game class, which encapsulates the core
 * game logic, including initialization, state management, and game loop control.
 * It also manages game-specific managers like ScoreManager.
 * @module Game
 */

import { Grid } from './models/Grid.js';
import { AnimalManager } from './managers/AnimalManager.js';
import { MatchManager } from './managers/MatchManager.js';
import { npcActionsConfig } from './config/npcActionsConfig.js';
import { NPCManager } from './managers/NPCManager.js';
import { npcConfig } from './config/npcConfig.js';
import { GameController } from './controllers/GameController.js';
import { MiniMergeController } from './controllers/MiniMergeController.js';
import { GameState } from './models/GameState.js';
import { DisasterManager } from './managers/DisasterManager.js';
import { EventEmitter } from './lib/EventEmitter.js';
import { WeatherManager } from './managers/WeatherManager.js';
import { BannerManager } from './managers/BannerManager.js';
import { TooltipManager } from './managers/TooltipManager.js';
import { TutorialManager } from './managers/TutorialManager.js';
import { AmbientAnimalManager } from './managers/AmbientAnimalManager.js';
import { ScoreManager } from './managers/ScoreManager.js';
import { ObstacleManager } from './managers/ObstacleManager.js';
import { RoadManager } from './managers/RoadManager.js';
import { SettlementManager } from './managers/SettlementManager.js';
import { 
    GAME_TYPE_QUICK_CLICK, 
    GAME_TYPE_CAMPAIGN_CLICK, 
    GAME_TYPE_CAMPAIGN_ROLLING, 
    GAME_TYPE_AUTOSAVE_QUICK,
    GAME_TYPE_AUTOSAVE_CAMPAIGN,
    GAME_TYPE_AUTOSAVE_ROLLING } from './constants/gameTypes.js';

export class Game {
    /**
     * The size of the buffer zone around the playable grid.
     * @type {number}
     */
    static BUFFER_SIZE = 2;
    /**
     * The number of items in the game's queue.
     * @type {number}
     */
    static QUEUE_SIZE = 5;
    /**
     * The number of turns before a new animal spawns.
     * @type {number}
     */
    static TURNS_PER_ANIMAL = 4;
    /**
     * The score awarded for trapping an animal.
     * @type {number}
     */
    static TRAP_SCORE = 50;

    /**
     * @param {object} dependencies - The dependencies for the Game class.
     * @param {import('./managers/UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('./managers/PlayerStatsManager.js').PlayerStatsManager} dependencies.playerStats - The player stats manager instance.
     * @param {import('./managers/CampaignManager.js').CampaignManager} dependencies.campaign - The campaign manager instance.
     * @param {object} dependencies.textConfig - The text configuration.
     * @param {import('./managers/ConfigManager.js').ConfigManager} dependencies.configManager - The config manager instance.
     * @param {import('./managers/DebugManager.js').DebugManager} dependencies.debug - The debug manager instance.
     * @param {import('./managers/AudioManager.js').AudioManager} dependencies.audioManager - The audio manager instance.
     * @param {import('./lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The event emitter instance.
     * @param {import('./managers/WeatherManager.js').WeatherManager} dependencies.weatherManager - The weather manager instance.
     * @param {object} dependencies.overlays - An object containing references to various overlay DOM elements.
     * @param {HTMLElement} dependencies.overlays.banner - The DOM element for the banner overlay.
     * @param {HTMLElement} dependencies.overlays.tooltip - The DOM element for the tooltip overlay.
     * @param {HTMLElement} dependencies.overlays.tutorial - The DOM element for the tutorial overlay.
     * @param {import('./managers/SaveManager.js').SaveManager} dependencies.saveManager - The save manager instance.
     * @param {HTMLElement} dependencies.overlays.ambientAnimal - The DOM element for the ambient animal overlay.
     */
    constructor(dependencies) {
        /** @private */
        this.ui = dependencies.ui;
        /** @private */
        this.playerStatsManager = dependencies.playerStats;
        /** @private */
        this.campaignManager = dependencies.campaign;
        /** @private */
        this.textConfig = dependencies.textConfig;
        /** @private */
        this.configManager = dependencies.configManager;
        /** @private */
        this.debugManager = dependencies.debug;
        /** @private */
        this.audioManager = dependencies.audioManager;
        /** @private */
        this.saveManager = dependencies.saveManager;
        /** @private */
        this.scoreManager = new ScoreManager();
        /** @private */
        this.eventEmitter = dependencies.eventEmitter;
        /** @private */
        this.bannerManager = new BannerManager({ bannerOverlay: dependencies.overlays.banner });
        /** @private */
        this.tooltipManager = new TooltipManager({ tooltipOverlay: dependencies.overlays.tooltip, playerStatsManager: this.playerStatsManager, eventEmitter: this.eventEmitter });
        /** @private */
        this.tutorialManager = new TutorialManager({
            eventEmitter: this.eventEmitter,
            ui: this.ui,
            bannerManager: this.bannerManager,
            tooltipManager: this.tooltipManager,
            playerStatsManager: this.playerStatsManager
        });
        /** @private */
        this.preferencesManager = dependencies.preferencesManager;
        /** @private */
        this.npcActionsConfig = npcActionsConfig;

        /** @private @type {Grid|null} */
        this.grid = null;
        /** @private @type {AnimalManager|null} */
        this.animalManager = null;
        /** @private @type {MatchManager|null} */
        this.matchManager = null;
        /** @private @type {AmbientAnimalManager|null} */
        this.ambientAnimalManager = null;
        /** @private @type {NPCManager|null} */
        this.npcManager = null;
        /** @private @type {NPCManager|null} */
        this.nonHumanNPCManager = null;
        /** @private @type {WeatherManager|null} */
        this.weatherManager = null;
        /** @private @type {RoadManager|null} */
        this.roadManager = null;
        /** @private @type {ObstacleManager|null} */
        this.obstacleManager = null;
        /** @private @type {SettlementManager|null} */
        this.settlementManager = null;
        /** @private @type {DisasterManager|null} */
        this.disasterManager = null;
        /** @private @type {GameController|null} */
        this.gameController = null;
        /** @private @type {GameState|null} */
        this.gameState = null;
        /** @private @type {number} */
        this.totalGridSize = 0;
        this.isDemoRunning = false;
        this.gameLoopId = null;
        this.lastTimestamp = 0;
        this.gameTickId = null;
        this.gameTickInterval = 200;
        /** @private */
        this.statsUpdateTickCounter = 0;
        /** @private */
        this.settlersAutosaveTickCounter = 0;
        /** @private */
        this.gemCount = 0;
        /** @private */
        this.isFpvMode = false;
        /** @private */
        this.fpvCamera = { x: 100, y: 0, z: 150, pitch: 0, yaw: 0, roll: 0, perspective: 200, originY: 50 }; // Initial camera state
        /** @private */
        this.fpvCameraElement = document.getElementById('fpv-camera');

        // --- Controllable NPC State ---
        this.controlledNpcId = null;
        this.controlledNpcManagerType = null;
        this.isNpcControlMode = false;
        /** @private @type {{npcId: number, managerType: string} | null} */
        this.hoveredNpc = null;


        this.eventEmitter.on('ui:miniMergeQueueClick', (index) => {
            // This listener lives for the lifetime of the Game object and delegates to the current controller.
            if (this.gameController instanceof MiniMergeController) this.gameController.handleQueueClick(index);
        });

        this.eventEmitter.on('ui:miniMergeNudge', () => {
            // This listener lives for the lifetime of the Game object and delegates to the current controller.
            if (this.gameController instanceof MiniMergeController) this.gameController.nudge();
        });



        
        this.eventEmitter.on('audio:soundEnded', ({ name }) => {
            // If the entrance sound for the current level has just finished, play the background music.
            if (this.gameState?.activeGameConfig?.entranceSound === name) {
                this.audioManager.play('level_music');
            }
        });

        // Listen for UI requests for NPC data to show tooltips
        this.eventEmitter.on('ui:requestNpcData', ({ npcId, managerType }) => {
            let npc = null;
            if (managerType === 'human' && this.npcManager) {
                npc = this.npcManager.npcs.get(npcId);
            } else if (managerType === 'non-human' && this.nonHumanNPCManager) {
                npc = this.nonHumanNPCManager.npcs.get(npcId);
            }
            if (npc) {
                this.eventEmitter.emit('ui:showNpcTooltip', { npc, managerType });
            }
        });

        this.eventEmitter.on('game:gemCreated', () => {
            this.gemCount++;
        });
        this.eventEmitter.on('game:gemsDestroyed', (count) => {
            this.gemCount -= count;
        });
        this.eventEmitter.on('game:levelWon', () => {
            this.handleLevelWin();
        });

        this.eventEmitter.on('ui:pauseNpcsForEditing', (shouldPause) => {
            if (this.npcManager) {
                this.npcManager.setPause(shouldPause);
            }
            if (this.nonHumanNPCManager) {
                this.nonHumanNPCManager.setPause(shouldPause);
            }
        });

        this.eventEmitter.on('ui:requestNpcEditor', ({ npcId, managerType }) => {
            let npc = null;
            if (managerType === 'human' && this.npcManager) {
                // Check both active NPCs and ex-inhabitants
                npc = this.npcManager.npcs.get(npcId) || this.npcManager.exInhabitants.get(npcId);
            } else if (managerType === 'non-human' && this.nonHumanNPCManager) {
                // Check both active NPCs and ex-inhabitants
                npc = this.nonHumanNPCManager.npcs.get(npcId) || this.nonHumanNPCManager.exInhabitants.get(npcId);
            }
            if (npc) {
                npc.managerType = managerType; // Attach the managerType to the object
                const allNpcConfigs = { ...this.configManager.masterItemsConfig.npcs, ...this.configManager.masterItemsConfig.nonHumanNpcs };
                this.ui.displayNpcEditor(npc, allNpcConfigs);
            }
        });

        this.eventEmitter.on('ui:requestSettlementEditor', ({ settlementKey }) => {
            const settlement = this.settlementManager.settlements.get(settlementKey);
            if (settlement) {
                this.ui.displaySettlementEditor(settlement);
            }
        });

        this.eventEmitter.on('game:batchUpdateSettlementProperties', ({ settlementKey, updates }) => {
            if (this.settlementManager) {
                this.settlementManager.batchUpdateSettlementProperties(settlementKey, updates);
            }
            // After properties are updated, immediately refresh the UI.
            const { allNpcs, allSettlements } = this._getLiveStatsAndNpcs();
            if (allNpcs) {
                this.ui.updateNpcSelector(allNpcs);
            }
            if (allSettlements) this.ui.updateSettlementSelector(allSettlements);

            // Force a re-render of the grid to show updated settlement inventories.
            this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
        });

        // --- New Dev Tools Listeners ---
        this.eventEmitter.on('ui:requestSpawnableAccessories', () => {
            if (this.npcManager) {
                const accessories = this.npcManager._getSpawnableAccessories();
                this.eventEmitter.emit('game:spawnableAccessoriesList', { accessories });
            }
        });
        this.eventEmitter.on('ui:requestLiveEntityCounts', () => {
            const counts = this.getLiveEntityCounts();
            this.eventEmitter.emit('game:liveEntityCounts', { counts });
        });
        this.eventEmitter.on('ui:requestRecyclableAccessories', () => {
            const accessories = this._getRecyclableAccessories();
            this.eventEmitter.emit('game:recyclableAccessoriesList', { accessories });
        });

        this.eventEmitter.on('debug:kickstartNpcs', () => {
            this.ui.kickstartNpcRendering();
        });

        this.eventEmitter.on('game:controlNpc', ({ npcId, managerType }) => {
            this.toggleNpcControl(npcId, managerType);
        });

        this.eventEmitter.on('ui:exitNpcControl', () => {
            if (this.isNpcControlMode) {
                this.toggleNpcControl(this.controlledNpcId, this.controlledNpcManagerType);
            }
        });

        this.eventEmitter.on('game:hoverNpc', ({ npcId, managerType, isHovering }) => {
            if (isHovering) {
                this.hoveredNpc = { npcId, managerType };
            } else {
                // Make sure we are un-hovering the correct NPC
                if (this.hoveredNpc && this.hoveredNpc.npcId === npcId && this.hoveredNpc.managerType === managerType) {
                    this.hoveredNpc = null;
                }
            }
        });
    }

    /**
     * Determines which accessories are recyclable.
     * An item is recyclable if it has no defined 'type' or if its type has no corresponding action in npcActionsConfig.
     * @returns {Array<object>} A list of recyclable accessory configurations.
     * @private
     */
    _getRecyclableAccessories() {
        // First, build a set of all item keys that are used as imports in any recipe.
        const allImportKeys = new Set();
        const settlements = Object.values(this.configManager.masterItemsConfig.settlements);
        for (const settlement of settlements) {
            if (settlement.recipes) {
                for (const recipe of settlement.recipes) {
                    if (recipe.imports) {
                        Object.keys(recipe.imports).forEach(key => allImportKeys.add(key));
                    }
                }
            }
        }

        const allAccessories = Object.values(this.configManager.masterItemsConfig.accessories);
        return allAccessories.filter(acc => {
            // An item is recyclable if it's NOT an import for any recipe AND it has no NPC action.
            const isAnImport = allImportKeys.has(acc.key);
            const hasAction = acc.type && this.npcActionsConfig[acc.type];
            return !isAnImport && !hasAction;
        });
    }

    /**
     * Creates and initializes an NPCManager instance.
     * @private
     * @param {object} npcTypeConfig - The configuration for the NPC type (human or non-human).
     * @param {HTMLElement} overlayElement - The DOM element for the NPC overlay.
     * @param {object} gameConfig - The active game configuration.
     * @returns {NPCManager} A new NPCManager instance.
     */ 
    _createNpcManager(npcTypeConfig, overlayElement) {
        const fullConfig = {
            ...npcTypeConfig,
            overlay: overlayElement
        };
        const npcManagerDeps = {
            ui: this.ui,
            debug: this.debugManager,
            npcTypeConfig: fullConfig,
            npcConfigs: Object.values(this.configManager.masterItemsConfig[npcTypeConfig.configKey] || {}),
            masterItemsConfig: this.configManager.masterItemsConfig,
            audioManager: this.audioManager,
            gameConfig: this.gameState.activeGameConfig,
            grid: this.grid,
            preferencesManager: this.preferencesManager,
            settlementManager: this.settlementManager, // Pass settlementManager to NPCManager
            game: this, // Pass the game instance
            obstacleManager: this.obstacleManager, // Pass obstacleManager to NPCManager
            isDemoRunning: this.isDemoRunning
        };
        return new NPCManager(npcManagerDeps);
    }

    /**
     * Sets up a new game session, initializing game state, grid, and managers.
     * @private
     * @param {object} gameConfig - The configuration for the new game.
     * @param {boolean} isCampaign - True if the game is part of a campaign, false otherwise.
     */
    _setupGameSession(gameConfig, isCampaign) {
        // Stop any existing game loops from the previous session to prevent memory leaks.
        this.stopGameLoop();

        // Explicitly nullify old managers to ensure they are garbage collected,
        // especially if they are not re-initialized in the new session.
        this.npcManager = null;
        this.nonHumanNPCManager = null;
        this.weatherManager = null;
        this.ambientAnimalManager = null;
        this.roadManager = null;
        this.obstacleManager = null;
        this.animalManager = null;

        // Destroy the old controller and its listeners before creating a new one.
        if (this.gameController) {
            this.gameController.destroy();
        }

        // Always remove the RTS GUI from a previous session before setting up a new one.
        this.ui.removeRtsGui();

        this.ui.toggleNudgeButton(false);
        // Always clear the weather overlay from a previous session
        this.ui.dom.weatherOverlay.innerHTML = '';

        this.gemCount = 0; // Reset gem count for the new session.
        this.settlersAutosaveTickCounter = 0; // Reset autosave timer for the new session.
        // Sync the config manager with the settings for the new game.
        // This ensures all managers created hereafter get the correct, active config.
        this.configManager.syncSettings(gameConfig);
        
        // TECH DEBT: `gridSize` is used inconsistently.
        // `ui.gridSize` refers to the TOTAL grid size including the buffer zone.
        this.ui.gridSize = gameConfig.gridSize + 2 * Game.BUFFER_SIZE;
        this.gameState = new GameState(
            { ...gameConfig, turnsPerAnimal: Game.TURNS_PER_ANIMAL, trapScore: Game.TRAP_SCORE },
            isCampaign, 
            Game.QUEUE_SIZE,
            this.eventEmitter
        );
        // TECH DEBT: `gridSize` within the game state and game config refers to the PLAYABLE area size.
        const playableGridSize = this.gameState.activeGameConfig.gridSize;

        this.totalGridSize = playableGridSize + 2 * Game.BUFFER_SIZE;

        // Initialize turn tracking based on game mode
        if (!isCampaign && this.configManager.settings.isLimitedTurnsMode) {
            this.gameState.numberOfTurnsRemaining = this.configManager.gameOptions.defaultTurnLimit;
            this.gameState.numberOfTurnsTaken = 0;
            this.gameState.isLimitedTurnsMode = this.configManager.settings.isLimitedTurnsMode;
        } else {
            this.gameState.numberOfTurnsRemaining = 0;
            this.gameState.numberOfTurnsTaken = 0;
            this.gameState.isLimitedTurnsMode = false;
        }

        // Initialize collector position for rolling mode
        if (gameConfig.gameMode === 'rolling') {
            const playableSize = playableGridSize;
            this.gameState.collectorPosition = Game.BUFFER_SIZE + Math.floor(playableSize / 2);
        }

        this.grid = new Grid(this.totalGridSize, Game.BUFFER_SIZE);


        /** @private */
        this.disasterManager = new DisasterManager({
            grid: this.grid, // Game.grid is available after Game is instantiated
            ui: this.ui,
            gameState: this.gameState,
            eventEmitter: this.eventEmitter,
            audioManager: this.audioManager
        });
        this.animalManager = null; // Reset and let it be created if needed

        this.obstacleManager = new ObstacleManager({
            grid: this.grid,
            ui: this.ui,
            debugManager: this.debugManager,
            configManager: this.configManager
        });

        this.roadManager = new RoadManager({
            grid: this.grid,
            ui: this.ui,
            debugManager: this.debugManager
        });

        const matchManagerDeps = {
            grid: this.grid,
            ui: this.ui,
            playerStatsManager: this.playerStatsManager,
            textConfig: this.textConfig,
            masterItemsConfig: this.configManager.masterItemsConfig,
            eventEmitter: this.eventEmitter
        };
        this.matchManager = new MatchManager(matchManagerDeps);

        // Create AnimalManager here, BEFORE the GameController is created.
        if (gameConfig.isAnimalsEnabled && gameConfig.gameMode !== 'rolling') {
            const animalManagerDeps = {
                grid: this.grid,
                ui: this.ui,
                gameConfig: this.gameState.activeGameConfig,
                textConfig: this.textConfig
            };
            this.animalManager = new AnimalManager(animalManagerDeps);
        }

        this.settlementManager = new SettlementManager({
            grid: this.grid,
            ui: this.ui,
            debug: this.debugManager,
            masterItemsConfig: this.configManager.masterItemsConfig,
            game: this, // Pass the game instance
            eventEmitter: this.eventEmitter
        });
        if (this.disasterManager) {
            this.disasterManager.reset();
        }

        if (this.preferencesManager.getPreference('isNpcWanderingEnabled', true)) {
            this.npcManager = this._createNpcManager(npcConfig.human, this.ui.dom.npcOverlay, this.gameState.activeGameConfig);
            this.nonHumanNPCManager = this._createNpcManager(npcConfig.nonHuman, this.ui.dom.nonHumanNPCOverlay, this.gameState.activeGameConfig);
        } else {
            this.npcManager = null;
            this.nonHumanNPCManager = null;
        }

        if (this.preferencesManager.getPreference('enableWeatherEffects', true)) {
            this.weatherManager = new WeatherManager({
                overlayElement: this.ui.dom.weatherOverlay,
                dayNightOverlay: this.ui.dom.dayNightOverlay,
                eventEmitter: this.eventEmitter,
                dayNightToggleBtn: this.ui.dom.dayNightToggleBtn,
                preferencesManager: this.preferencesManager,
                game: this,
                gameController: this.gameController
            });
            
        } else {
            this.weatherManager = null;
        }

        if (this.preferencesManager.getPreference('enableAmbientAnimals', true)) {
            this.ambientAnimalManager = new AmbientAnimalManager({
                overlayElement: this.ui.dom.ambientAnimalOverlay,
                eventEmitter: this.eventEmitter
            });
            
        }

        this.gameState.activeGameConfig.allItems = [
            ...this.gameState.activeGameConfig.upgradePath,
            ...this.gameState.activeGameConfig.genericUpgradePath,
            ...this.gameState.activeGameConfig.animals,
            ...(this.gameState.activeGameConfig.stationaryObjects || []),
            ...Object.values(this.configManager.masterItemsConfig.powerups || {})
        ];

        if (gameConfig.isMiniMerge) {
            this.gameController = new MiniMergeController({
                game: this,
                gameState: this.gameState,
                grid: this.grid,
                animalManager: this.animalManager,
                matchManager: this.matchManager,
                textConfig: this.textConfig,
                ui: this.ui,
                onGameOver: () => this.handleGameOver(),
                totalGridSize: this.totalGridSize,
                settlementManager: this.settlementManager,
                eventEmitter: this.eventEmitter,
                disasterManager: this.disasterManager
            });
        } else {
            this.gameController = new GameController({
                game: this,
                gameState: this.gameState,
                grid: this.grid,
                animalManager: this.animalManager,
                matchManager: this.matchManager,
                textConfig: this.textConfig,
                ui: this.ui,
                onGameOver: () => this.handleGameOver(),
                totalGridSize: this.totalGridSize,
                settlementManager: this.settlementManager,
                eventEmitter: this.eventEmitter,
                disasterManager: this.disasterManager
            });
        }

        this.gameController.initController();
        this.gameController.resetToolMode(); // Reset tool mode on game setup
        this.ui.toggleNudgeButton(gameConfig.isMiniMerge);
        this.ui.toggleSaveButton(true);

        // Create RTS GUI if in Settlers Mode
        if (gameConfig.isSettlersModeEnabled) {
            this.ui.createRtsGui(
                (settlementKey) => {
                    // Callback for when a settlement icon is clicked
                    this.gameController.togglePlacementMode(settlementKey);
                },
                () => {
                    // Callback for when the delete button is clicked
                    this.gameController.toggleDeleteMode();
                },
                () => {
                    // Callback for when the pause button is clicked
                    this.gameController.togglePauseMode();
                }
            );
            // In Settlers Mode, the game is always "paused" from a turn-based perspective.
            this.gameController.isPaused = true;
            this.ui.updateRtsPauseButton(true);
        }
    }

    /**
     * Initializes or resets the game with a new configuration.
     * @param {object} gameConfig - The configuration for the game.
     * @param {boolean} [isCampaign=false] - True if the game is part of a campaign, false otherwise.
     */
    initGameSession(gameConfig, isCampaign = false, isRestoring = false) {
        if (gameConfig.isMiniMerge) {
            this.configManager.syncSettings(gameConfig);
            gameConfig = this.configManager.getActiveGameConfig();
        }
        // --- Settlers Mode Overrides ---
        if (gameConfig.isSettlersModeEnabled) {
            gameConfig.isAnimalsEnabled = false;
            gameConfig.isDisastersEnabled = false;
            this.ui.dom.gemQueueBox.classList.add('hidden');
            this.ui.dom.mainScores.classList.add('hidden');
            this.ui.dom.turnCounter.classList.add('hidden');
        }

        this._setupGameSession(gameConfig, isCampaign);
       
        if (this.animalManager) {
            this.animalManager.initManager();
        }
        if (this.npcManager) this.npcManager.initManager();
        if (this.nonHumanNPCManager) this.nonHumanNPCManager.initManager();
        if (this.settlementManager) this.settlementManager.initManager();
        if (this.roadManager) this.roadManager.initManager();
        if (this.obstacleManager) this.obstacleManager.initManager();

        this.grid.populateBuffer(this.gameState.activeGameConfig.bufferIcons);
        this.placeStationaryObjects();

        // Initialize gem count to 0. It will be updated if the grid is pre-filled.
        this.gemCount = 0;
        if (gameConfig.gameMode === 'rolling' && !isRestoring) {
            this.gemCount = this._prefillRollingGrid();
        }

        this.ui.updateScore(this.gameState.score);
        // Only update queue and turn-based UI if not in Settlers Mode
        if (!gameConfig.isSettlersModeEnabled) {
            this.ui.dom.gemQueueBox.classList.remove('hidden');
            this.ui.dom.mainScores.classList.remove('hidden');
            this.ui.dom.turnCounter.classList.remove('hidden');
            const turnsUntilAnimal = this.animalManager ? this.animalManager.getTurnsUntilNextSpawn() : -1;
            const nextAnimal = this.animalManager ? this.animalManager.getNextAnimalToSpawn() : null;
            this.ui.updateQueue(this.gameState.itemQueue, turnsUntilAnimal, nextAnimal, gameConfig.gameMode);
            if (this.gameState.isLimitedTurnsMode) {
                this.ui.updateNumberOfTurns(this.gameState.numberOfTurnsRemaining);
                this.ui.updateTargetScore(null); // Hide target score if using turns
            } else {
                this.ui.updateNumberOfTurns(this.gameState.numberOfTurnsTaken); // Show turns without limit
                this.ui.updateTargetScore(this.gameState.activeGameConfig.targetScore);
            }
        }
        this.ui.displayHighScores(this.scoreManager.getScores());
        this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
        
        
         // Show the stats box for game modes that use it.
        this.ui.toggleStatsBox(true);

        
        this.ui.displayMessage(this.textConfig.gameMessages.gameStart);
        this.weatherManager?.setGameConfig(gameConfig);
        this.ambientAnimalManager?.setGameConfig(gameConfig);

        if (gameConfig.entranceSound) {
            this.audioManager.play(gameConfig.entranceSound);
        } else {
            // If there's no specific entrance sound, start the general level music immediately.
            this.audioManager.play('level_music');
        }

        // Don't show the level start banner on the very first game, as the tutorial will show a "Welcome" banner.
        if (!this.playerStatsManager.isFirstPlay(false)) { // `false` to not consume the "first play" status
            const isRolling = gameConfig.gameMode === 'rolling';
            const campaignName = isRolling ? 'Rolling' : 'Campaign';
            const bannerContent = this.gameState.isCampaignLevel
                ? `${campaignName} - Level ${this.campaignManager.getCurrentLevel() + 1}`
                : 'Quick Game';
            this.bannerManager.show(bannerContent);
        }
        this.eventEmitter.emit('game:start', { isCampaign, gameMode: gameConfig.gameMode });
    }

    /**
     * Pre-fills the grid with random items for Rolling Grid mode.
     * @returns {number} The number of gems added to the grid.
     * @private
     */
    _prefillRollingGrid() {
        let gemsAdded = 0;
        const startRow = this.grid.bufferSize;
        for (let r = startRow; r < this.grid.size - this.grid.bufferSize; r++) {
            for (let c = this.grid.bufferSize; c < this.grid.size - this.grid.bufferSize; c++) {
                // Use the configured chance to place an item to keep it sparse
                if (Math.random() < this.gameState.activeGameConfig.rollingGridPrefillChance) {
                    const item = this.gameState.getRandomQueueItem(false); // No power-ups on initial fill
                    this.grid.set(r, c, item);
                    gemsAdded++;
                }
            }
        }
        return gemsAdded;
    }

    /**
     * Places stationary objects on the grid based on the game configuration.
     */
    placeStationaryObjects() {
        const config = this.gameState.activeGameConfig;
        if (!config.isStationaryObjectsEnabled) { return; }

        if (this.isDemoRunning) {
            // --- Demo Mode: Force specific settlements ---
            const forcedSettlements = ['settlement_lake', 'settlement_farm', 'settlement_bakery', 'settlement_warehouse'];
            const allSettlementConfigs = this.configManager.masterItemsConfig.settlements;

            for (const key of forcedSettlements) {
                const settlementConfig = allSettlementConfigs[key];
                if (settlementConfig) {
                    const cell = this.grid.getRandomEmptyCell();
                    if (!cell) {
                        this.debugManager.log(`Could not place forced settlement ${key}, no empty cells left.`);
                        break; // No more room
                    }
                    this.settlementManager.createSettlement(cell.r, cell.c, settlementConfig);
                }
            }
        } else if (config.isSettlersModeEnabled) {
            // --- Settlers Mode: Place the first 3 defined settlements ---
            const allSettlementConfigs = Object.values(this.configManager.masterItemsConfig.settlements);
            const settlementsToPlace = allSettlementConfigs.slice(0, 3);

            this.debugManager.log(`Settlers Mode: Placing initial ${settlementsToPlace.length} settlements.`);

            for (const settlementConfig of settlementsToPlace) {
                const cell = this.grid.getRandomEmptyCell();
                if (!cell) {
                    this.debugManager.log(`Could not place settlement ${settlementConfig.name}, no empty cells left.`);
                    break; // No more room
                }
                this.settlementManager.createSettlement(cell.r, cell.c, settlementConfig);
            }

        } else {
            // --- Regular Mode: Place random settlements ---
            const settlementConfigs = config.stationaryObjects;
            if (!settlementConfigs || settlementConfigs.length === 0) return;

            const numToPlace = Math.floor(this.grid.playableSize / 2);

            for (let i = 0; i < numToPlace; i++) {
                const cell = this.grid.getRandomEmptyCell();
                if (!cell) { break; } // Stop if no more empty cells
                const settlementConfig = settlementConfigs[Math.floor(Math.random() * settlementConfigs.length)];
                this.settlementManager.createSettlement(cell.r, cell.c, settlementConfig);
            }
        }
    }

    /**
     * Handles the game over state, including high score submission.
     */
    handleGameOver() {
        this.audioManager.stop('background');
        this.audioManager.play('lose');
        this.gameState.isGameOver = true;
        this.handleGameLoss();
        this.eventEmitter.emit('achievement:gameLost', { gameState: this.gameState });
        if (this.scoreManager.isHighScore(this.gameState.score)) {
            this.ui.promptForInitials(this.gameState.score, (initials) => {
                this.scoreManager.addScore(this.gameState.score, initials);
                this.ui.displayHighScores(this.scoreManager.getScores());
                this.ui.displayMessage(this.textConfig.gameMessages.gameOverHighScore.replace('{SCORE}', this.gameState.score));
            });
        } else {
            this.ui.displayMessage(this.textConfig.gameMessages.gameOver.replace('{SCORE}', this.gameState.score));
        }
    }

    /**
     * Handles the logic when a campaign level is won.
     */
    handleLevelWin() {
        this.audioManager.play('win');
        this.ui.showLevelComplete(() => this.eventEmitter.emit('game:advanceLevel'));
    }

    /**
     * Handles the logic when the game is lost.
     */
    handleGameLoss() {
        const gameMode = this.gameState.activeGameConfig.gameMode;
        const isCampaign = this.gameState.isCampaignLevel;
        let saveTypeToDelete;
        if (isCampaign) {
            saveTypeToDelete = gameMode === 'rolling' ? GAME_TYPE_CAMPAIGN_ROLLING : GAME_TYPE_CAMPAIGN_CLICK;
        } else {
            saveTypeToDelete = GAME_TYPE_QUICK_CLICK;
        }
        this.eventEmitter.emit('game:deleteSave', saveTypeToDelete);
    }

    /**
     * Advances the campaign to the next level and emits an event for the App to handle the transition.
     */
    advanceToNextLevel() {
        this.campaignManager.advanceLevel();
        if (!this.gameState.saveSlotId) {
            console.error('Cannot advance level: saveSlotId is missing from gameState.');
            return;
        }
        const savedSlot = this.saveManager.getSaveSlotById(this.gameState.saveSlotId);
        const nextLevelSettings = {
            gameMode: savedSlot.gameState.configSettings.gameMode
        };
        
        // Delete the old save and emit an event for the App to start the new level.
        this.eventEmitter.emit('game:deleteSave', savedSlot.type);
        const nextLevelIndex = this.campaignManager.getCurrentLevel();
        this.eventEmitter.emit('game:startNextLevel', { levelIndex: nextLevelIndex, settings: nextLevelSettings, saveType: savedSlot.type });
    }

    /**
     * Determines the correct autosave type based on the current game state and triggers a save.
     */
    autoSave() {
        if (this.isDemoRunning) return; // Do not autosave during demo

        let saveType;
        if (this.gameState.isCampaignLevel) {
            if (this.gameState.activeGameConfig.gameMode === 'rolling') {
                saveType = GAME_TYPE_AUTOSAVE_ROLLING;
            } else {
                saveType = GAME_TYPE_AUTOSAVE_CAMPAIGN;
            }
        } else {
            saveType = GAME_TYPE_AUTOSAVE_QUICK;
        }
        this.saveGame(saveType);
    }

    /**
     * Saves the current game state to a specified save slot type.
     * @param {string} type - The type of game to save (e.g., GAME_TYPE_QUICK_CLICK).
     */
    saveGame(type) {
        if (this.isDemoRunning) {
            this.debugManager.log('Saving is disabled during demo mode.');
            return;
        }

        if (this.gameState.isGameOver) {
            this.ui.displayMessage(this.textConfig.gameMessages.saveFailed);
            return;
        }

        const activeConfig = this.gameState.activeGameConfig;
        const configSettings = {
            biomeKey: activeConfig.biomeKey,
            pathLength: activeConfig.pathLength,
            gridSize: activeConfig.gridSize,
            gameMode: activeConfig.gameMode,
            isAnimalsEnabled: activeConfig.isAnimalsEnabled,
            isDisastersEnabled: activeConfig.isDisastersEnabled,
            isRandomQueueEnabled: activeConfig.isRandomQueueEnabled,
            isStationaryObjectsEnabled: activeConfig.isStationaryObjectsEnabled,
            isPowerupsEnabled: activeConfig.isPowerupsEnabled,
            isSettlersModeEnabled: activeConfig.isSettlersModeEnabled
        };

        const gameStatePayload = {
            configSettings: configSettings,
            gameData: this.getGameState()
        };

        const level = this.gameState.isCampaignLevel ? this.campaignManager.getCurrentLevel() : null;
        this.gameState.saveSlotId = this.saveManager.saveGameByType(gameStatePayload, type, level);
        this.ui.displayMessage(this.textConfig.gameMessages.gameSaved);
        this.eventEmitter.emit('ui:saveSlotsChanged');
    }

    /**
     * Gets the current game state for saving.
     * @returns {object} An object containing the current score, grid data, item queue, and other game state information.
     */
    getGameState() {
        const slimGridData = this.grid.data.map(row => {
            return row.map(cell => {
                if (!cell) return null;
                if (cell.isBuffer) return { b: 1, d: cell.display };
                const slimCell = { k: cell.key };
                if (cell.turnsUntilMove !== undefined) {
                    slimCell.t = cell.turnsUntilMove;
                }
                if (cell.id !== undefined) {
                    slimCell.i = cell.id;
                }
                return slimCell;
            });
        });
        const slimQueue = this.gameState.itemQueue.map(item => ({ k: item.key }));
        return {
            score: this.gameState.score,
            gridData: slimGridData,
            itemQueue: slimQueue,
            isCampaignLevel: this.gameState.isCampaignLevel,
            animalState: this.animalManager ? this.animalManager.getState() : null,
            disasterState: this.disasterManager.getState(),
            numberOfTurnsTaken: this.gameState.numberOfTurnsTaken,
            numberOfTurnsRemaining: this.gameState.numberOfTurnsRemaining,
            isLimitedTurnsMode: this.gameState.isLimitedTurnsMode,
            sessionMerges: this.gameState.sessionMerges,
            cratesBuilt: this.gameState.cratesBuilt,
            powerupsUsed: this.gameState.powerupsUsed,
            disastersSurvived: this.gameState.disastersSurvived,
            saveSlotId: this.gameState.saveSlotId,
            settlementState: this.settlementManager ? this.settlementManager.getState() : null,
            npcState: this.npcManager ? this.npcManager.getState() : null,
            nonHumanNpcState: this.nonHumanNPCManager ? this.nonHumanNPCManager.getState() : null,
            obstacleState: this.obstacleManager ? this.obstacleManager.getState() : null,
            roadState: this.roadManager ? this.roadManager.getState() : null
        };
    }

    /**
     * Restores the game state from a saved state.
     * @param {object} state - The saved game state object.
     * @param {object} gameConfig - The game configuration associated with the saved state.
     */
    restoreFromState(state, gameConfig, saveSlotId) {
        this._setupGameSession(gameConfig, state.isCampaignLevel);

        // Restore state to the new GameState model
        this.gameState.score = state.score;
        this.gameState.numberOfTurnsRemaining = state.numberOfTurnsRemaining;
        this.gameState.numberOfTurnsTaken = state.numberOfTurnsTaken;
        this.gameState.isLimitedTurnsMode = state.isLimitedTurnsMode; // Restore turn limit
        this.gameState.cratesBuilt = state.cratesBuilt || 0;
        this.gameState.sessionMerges = state.sessionMerges || 0;
        this.gameState.powerupsUsed = state.powerupsUsed || 0;
        this.gameState.disastersSurvived = state.disastersSurvived || 0;
        this.gameState.saveSlotId = saveSlotId;
        
        this.gameState.itemQueue = state.itemQueue.map(savedItem => this.gameState.activeGameConfig.allItems.find(item => item.key === savedItem.k));

        const findLiveItem = (savedItem) => {
            if (!savedItem) return null;
            if (savedItem.b) {
                return { display: savedItem.d, isBuffer: true };
            }
            let liveItem = null;
            // Search through all categories in masterItemsConfig to find the item
            for (const category in this.configManager.masterItemsConfig) {
                const item = Object.values(this.configManager.masterItemsConfig[category]).find(i => i.key === savedItem.k);
                if (item) { liveItem = item; break; }
            }

            if (!liveItem) return null;
            const newLiveItem = { ...liveItem };
            if (savedItem.t !== undefined) { newLiveItem.turnsUntilMove = savedItem.t; }
            if (savedItem.i !== undefined) { newLiveItem.id = savedItem.i; }
            return newLiveItem;
        };

        // Create AnimalManager here, AFTER gameConfig is set up but BEFORE restoring its state.
        if (gameConfig.isAnimalsEnabled && gameConfig.gameMode !== 'rolling') {
            const animalManagerDeps = {
                grid: this.grid,
                ui: this.ui,
                gameConfig: this.gameState.activeGameConfig,
                textConfig: this.textConfig
            };
            this.animalManager = new AnimalManager(animalManagerDeps);
            this.gameController.animalManager = this.animalManager; // Update controller reference
        }
        if (state.animalState && this.animalManager) {
            // No initManager() here, as restoreState handles setup from save data.
            this.animalManager.restoreState(state.animalState);
        }
        for (let r = 0; r < this.totalGridSize; r++) {
            for (let c = 0; c < this.totalGridSize; c++) {
                const savedItem = state.gridData[r][c];
                // When restoring, we skip placing settlements here because the SettlementManager
                // will handle creating its own instances from its dedicated state object.
                if (savedItem?.k && savedItem.k.startsWith('settlement_')) {
                    continue;
                }
                const liveItem = findLiveItem(savedItem);
                this.grid.set(r, c, liveItem);
            }
        }
        
        // Restore settlement state AFTER the main grid loop
        if (state.settlementState && this.settlementManager) {
            this.settlementManager.restoreState(state.settlementState);
        }

        if (state.npcState && this.npcManager) {
            this.npcManager.restoreState(state.npcState);
        }
        if (state.nonHumanNpcState && this.nonHumanNPCManager) {
            this.nonHumanNPCManager.restoreState(state.nonHumanNpcState);
        }
        if (state.obstacleState && this.obstacleManager) {
            // Check if there are actually obstacles to restore.
            if (state.obstacleState.obstacles && state.obstacleState.obstacles.length > 0) {
                this.obstacleManager.restoreState(state.obstacleState);
            } else {
                this.obstacleManager.initManager();
            }
        } else if (this.obstacleManager) { // Handle saves made before the obstacle manager existed.
            // If loading an old save without obstacle data, generate new ones.
            this.obstacleManager.initManager();
        }
        if (state.roadState && this.roadManager) {
            // Check if there are actually roads to restore.
            if (state.roadState.roadTiles && state.roadState.roadTiles.length > 0) {
                this.roadManager.restoreState(state.roadState);
            } else {
                this.roadManager.initManager();
            }
        } else if (this.roadManager) { // Handle saves made before the road manager existed.
            // If loading an old save without road data, generate new ones.
            this.roadManager.initManager();
        }

        // After restoring all items, we need to re-calculate the initial gem count.
        this.gemCount = 0;
        for (const row of this.grid.data) {
            for (const cell of row) {
                // A "gem" is any item that isn't a special entity like a settlement, NPC, animal, or buffer icon.
                if (cell && !cell.isStationary && !cell.isAnimal && !cell.isNpc && !cell.isBuffer) this.gemCount++;
            }
        }

        this.ui.updateScore(this.gameState.score);

        // Apply Settlers Mode UI overrides on restore
        if (gameConfig.isSettlersModeEnabled) {
            this.ui.dom.gemQueueBox.classList.add('hidden');
            this.ui.dom.mainScores.classList.add('hidden');
            this.ui.dom.turnCounter.classList.add('hidden');
            this.grid.domElement.classList.add('settlers-mode-active');
        } else {
            this.ui.dom.gemQueueBox.classList.remove('hidden');
            this.ui.dom.mainScores.classList.remove('hidden');
            this.ui.dom.turnCounter.classList.remove('hidden');
            this.ui.updateQueue(this.gameState.itemQueue, undefined, undefined, gameConfig.gameMode);
            if (this.gameState.isLimitedTurnsMode) {
                this.ui.updateNumberOfTurns(this.gameState.numberOfTurnsRemaining);
                this.ui.updateTargetScore(null);
            } else {
                this.ui.updateNumberOfTurns(this.gameState.numberOfTurnsTaken);
                this.ui.updateTargetScore(this.gameState.activeGameConfig.targetScore);
            }
        }
        if (state.disasterState && state.disasterState.activeDisaster) {
            this.disasterManager.restoreState(state.disasterState);
        }

        // After all state is restored, update the UI to reflect the loaded grid.
        this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
        this.ui.displayMessage(this.textConfig.gameMessages.gameLoaded);

        this.ambientAnimalManager?.setGameConfig(gameConfig);

        // Don't show the level start banner if we are restoring the very first game session.
        if (this.playerStatsManager.stats.hasPlayedBefore) {
            const bannerContent = state.isCampaignLevel
                ? `Level ${this.campaignManager.getCurrentLevel() + 1}`
                : 'Quick Game';
            this.bannerManager.show(bannerContent);
        }
    }

    /**
     * Checks if the current level's win condition has been met.
     * @returns {boolean} True if the level is completed, false otherwise.
     */
    isLevelCompleted() {
        if (this.gameState.isCampaignLevel && this.gameState.score >= this.gameState.activeGameConfig.targetScore) {
            const levelIndex = this.campaignManager.getCurrentLevel();
            this.eventEmitter.emit('achievement:levelWon', { gameState: this.gameState, levelIndex: levelIndex });
            return true;
        }
        return false;
    }

    /**
     * The main game loop, driven by requestAnimationFrame for animations.
     * @param {number} timestamp - The current time provided by requestAnimationFrame.
     */
    gameLoop(timestamp) {
        if (this.gameState.isGameOver) {
            this.stopGameLoop();
            return;
        }

        if (!this.lastTimestamp) {
            this.lastTimestamp = timestamp;
        }
        const deltaTime = timestamp - this.lastTimestamp;
        this.lastTimestamp = timestamp;

        // --- UPDATE VISUAL MANAGERS ---
        this.ui.updateTemporaryEffectsLifecycle();

        if (this.weatherManager) this.weatherManager.update(deltaTime);
        if (this.ambientAnimalManager) this.ambientAnimalManager.update(deltaTime);

        this.gameLoopId = requestAnimationFrame(this.boundGameLoop);
    }

    /**
     * The main game logic tick, driven by setInterval for game state updates.
     */
    gameTick() {
        if (this.gameState.isGameOver || (this.gameController && this.gameController.isProcessingTurn)) {
            return;
        }

        if (this.npcManager) this.npcManager.update();
        if (this.nonHumanNPCManager) this.nonHumanNPCManager.update();
        if (this.settlementManager) this.settlementManager.update();

        // Throttle stats updates to run once per second (every 5 ticks).
        this.statsUpdateTickCounter++;
        if (this.statsUpdateTickCounter >= 5) {
            const { stats, allNpcs, allSettlements } = this._getLiveStatsAndNpcs();
            this.ui.updateGameStats(stats);
            if (allNpcs) this.ui.updateNpcSelector(allNpcs);
            if (allSettlements) this.ui.updateSettlementSelector(allSettlements);
            this.statsUpdateTickCounter = 0;
        }

        // --- Settlers Mode Autosave ---
        if (this.gameState.activeGameConfig.isSettlersModeEnabled) {
            this.settlersAutosaveTickCounter++;
            // 600 ticks = 120 seconds = 2 minutes (at 200ms per tick)
            if (this.settlersAutosaveTickCounter >= 600) {
                this.autoSave();
                this.settlersAutosaveTickCounter = 0;
            }
        }
    }

    /**
     * Gathers live statistics and lists of all NPCs and settlements.
     * @returns {{stats: object, allNpcs: Array<object>, allSettlements: Array<object>}} An object containing stats and entity lists.
     * @private
     */
    _getLiveStatsAndNpcs() {
        const humanNpcs = this.npcManager ? Array.from(this.npcManager.npcs.values()).map(npc => ({ ...npc, managerType: 'human' })) : [];
        const humanExInhabitants = this.npcManager ? Array.from(this.npcManager.exInhabitants.values()).map(npc => ({ ...npc, managerType: 'human', isEx: true })) : [];

        const nonHumanNpcs = this.nonHumanNPCManager ? Array.from(this.nonHumanNPCManager.npcs.values()).map(npc => ({ ...npc, managerType: 'non-human' })) : [];
        const nonHumanExInhabitants = this.nonHumanNPCManager ? Array.from(this.nonHumanNPCManager.exInhabitants.values()).map(npc => ({ ...npc, managerType: 'non-human', isEx: true })) : [];

        const allNpcs = [...humanNpcs, ...nonHumanNpcs, ...humanExInhabitants, ...nonHumanExInhabitants];

        const allSettlements = this.settlementManager ? Array.from(this.settlementManager.settlements.values()) : [];
        const stats = this._getLiveStats(humanNpcs.length, nonHumanNpcs.length);

        return { stats, allNpcs, allSettlements };
    }

    /**
     * Gathers live statistics from various game managers.
     * @returns {object} An object containing various game stats.
     * @private
     */
    _getLiveStats(humanNpcCount, nonHumanNpcCount) {
        if (!this.grid) return {};

        // Get counts directly from the managers and the game state.
        const animals = this.animalManager ? this.animalManager.getAnimalCount() : 0;
        const settlements = this.settlementManager ? this.settlementManager.settlements.size : 0;
        const emptyCells = this.grid.getEmptyCellCount();

        return {
            animals: animals,
            settlements: settlements,
            humanNpcs: humanNpcCount,
            nonHumanNpcs: nonHumanNpcCount,
            gems: this.gemCount,
            emptyCells: emptyCells,
            biomeName: this.gameState.activeGameConfig.name,
            gridSize: `${this.grid.playableSize}x${this.grid.playableSize}`
        };
    }




    /**
     * Gathers a comprehensive count of all entities currently in the game.
     * This is intended for debugging and can be called from the console.
     * @returns {object} An object containing counts of all entities.
     */
    getLiveEntityCounts() {
        const counts = {
            itemsOnGrid: this.gemCount,
            humanNpcs: this.npcManager ? this.npcManager.getNpcCount() : 0,
            nonHumanNpcs: this.nonHumanNPCManager ? this.nonHumanNPCManager.getNpcCount() : 0,
            animalsOnGrid: this.animalManager ? this.animalManager.getAnimalCount() : 0,
            settlements: this.settlementManager ? this.settlementManager.settlements.size : 0,
            itemsInInventories: {},
            itemsHeldByNpcs: {}
        };

        // Count items in settlement inventories
        if (this.settlementManager) {
            for (const settlement of this.settlementManager.settlements.values()) {
                for (const [itemKey, quantity] of Object.entries(settlement.imports)) {
                    counts.itemsInInventories[itemKey] = (counts.itemsInInventories[itemKey] || 0) + quantity;
                }
                for (const [itemKey, quantity] of Object.entries(settlement.exports)) {
                    counts.itemsInInventories[itemKey] = (counts.itemsInInventories[itemKey] || 0) + quantity;
                }
            }
        }

        // Count items held by NPCs
        if (this.npcManager) {
            for (const npc of this.npcManager.npcs.values()) {
                if (npc.inventory.accessory) {
                    counts.itemsHeldByNpcs[npc.inventory.accessory.key] = (counts.itemsHeldByNpcs[npc.inventory.accessory.key] || 0) + 1;
                }
            }
        }

        // Add a total count for convenience
        counts.totalItemsInWorld =
            counts.itemsOnGrid +
            Object.values(counts.itemsInInventories).reduce((a, b) => a + b, 0) +
            Object.values(counts.itemsHeldByNpcs).reduce((a, b) => a + b, 0);

        counts.totalEntities =
            counts.totalItemsInWorld +
            counts.humanNpcs + counts.nonHumanNpcs + counts.animalsOnGrid + counts.settlements;

        return counts;
    }

    /**
     * Starts the main game loop and the game tick loop.
     */
    startGameLoop() {
        if (this.gameLoopId) return;

        // Bind the game loop function once, if it hasn't been already.
        if (!this.boundGameLoop) {
            this.boundGameLoop = this.gameLoop.bind(this);
        }

        // Reset timestamp to ensure correct deltaTime on the first frame
        this.lastTimestamp = 0;
        this.gameLoopId = requestAnimationFrame(this.boundGameLoop);
        this.startGameTick();
    }

    /**
     * Stops the main game loop and the game tick loop.
     */
    stopGameLoop() {
        if (this.gameLoopId) {
            cancelAnimationFrame(this.gameLoopId);
            this.gameLoopId = null;
        }
        this.stopGameTick();
    }

    /**
     * Starts the game logic tick loop.
     * @private
     */
    startGameTick() {
        if (this.gameTickId) return;
        if (!this.boundGameTick) {
            this.boundGameTick = this.gameTick.bind(this);
        }
        this.gameTickId = setInterval(this.boundGameTick, this.gameTickInterval);
    }

    /**
     * Stops the game logic tick loop.
     * @private
     */
    stopGameTick() {
        if (this.gameTickId) {
            clearInterval(this.gameTickId);
            this.gameTickId = null;
        }
    }

    // --- First Person View (FPV) Methods ---

    /**
     * Toggles the First-Person View mode.
     */
    toggleFirstPersonView() {
        this.isFpvMode = !this.isFpvMode;
        const appContainer = this.ui.dom.appContainer;
        const gamePage = document.getElementById('game-page');
        const gameGridWrapper = this.ui.dom.gameGridWrapper;

        if (this.isFpvMode) {
            // Reset the camera state to your desired view
            this.fpvCamera = { x: 0, y: 0, z: 0, pitch: 90, yaw: 0, roll: 0, perspective: 200, originY: 50 };
            
            appContainer.classList.add('fpv-active');
            gamePage.classList.add('fpv-active');           
            gameGridWrapper.classList.remove('game-grid-wrapper-tilt');
            this.fpvCameraElement.classList.remove('hidden');
            // The keydown listener is now in eventManager.js, so we just need to listen for the specific event.
            this.eventEmitter.on('keydown:arrow', this.handleFpvMovement.bind(this));
            this.updateFpvCamera();
        } else {
            appContainer.classList.remove('fpv-active');
            gamePage.classList.add('fpv-active');
            gameGridWrapper.classList.add('game-grid-wrapper-tilt');
            this.fpvCameraElement.classList.add('hidden');
            this.eventEmitter.off('keydown:arrow', this.handleFpvMovement.bind(this));
            // Reset inline styles applied during FPV mode
            gameContainer.style.transform = '';
            appContainer.style.removeProperty('--fpv-perspective');
            appContainer.style.removeProperty('--fpv-origin');
        }
    }

    /**
     * Handles camera movement with arrow keys in FPV mode.
     * @param {KeyboardEvent} e - The keydown event.
     */
    handleFpvMovement(e) {
        if (!this.isFpvMode) return;

        const moveSpeed = 20;
        const turnSpeed = 3;

        switch (e.key) {
            // --- LOOKING ---
            case 'ArrowUp':
                this.fpvCamera.pitch += turnSpeed; // Look down
                break;
            case 'ArrowDown':
                this.fpvCamera.pitch -= turnSpeed; // Look up
                break;
            case 'ArrowLeft':
                this.fpvCamera.yaw -= turnSpeed; // Lean left
                break;
            case 'ArrowRight':
                this.fpvCamera.yaw += turnSpeed; // Lean right
                break;

    
            // --- MOVEMENT ---
            case 's': // BACKWARDS
                this.fpvCamera.x += Math.sin(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                this.fpvCamera.z -= Math.cos(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                break;
            case 'w': // FORWARDS
                this.fpvCamera.x -= Math.sin(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                this.fpvCamera.z += Math.cos(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                break;
            case 'a': // STRAFE LEFT
                this.fpvCamera.x -= Math.cos(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                this.fpvCamera.z -= Math.sin(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                break;
            case 'd': // STRAFE RIGHT
                this.fpvCamera.x += Math.cos(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                this.fpvCamera.z += Math.sin(this.fpvCamera.yaw * Math.PI / 180) * moveSpeed;
                break;
            case 'q': // UPWARDS
                this.fpvCamera.y -= moveSpeed;
                break;
            case 'e': // DOWNWARDS
                this.fpvCamera.y += moveSpeed;
                break;
            case 'z': // PIVOT LEFT
                this.fpvCamera.roll += turnSpeed;
                break;
            case 'c': // PIVOT RIGHT
                this.fpvCamera.roll -= turnSpeed;
                break;
            
            // --- PERSPECTIVE ADJUSTMENT ---
            case 'i': // Increase perspective (zoom out)
                this.fpvCamera.perspective += 25;
                break;
            case 'k': // Decrease perspective (zoom in)
                this.fpvCamera.perspective -= 25;
                break;
            case 'u': // Move perspective origin up
                this.fpvCamera.originY += 5;
                break;
            case 'j': // Move perspective origin down
                this.fpvCamera.originY -= 5;
                break;
        }
        this.updateFpvCamera();
    }

    /**
     * Updates the CSS transform of the game world based on the camera's position and orientation.
     */
    updateFpvCamera() {
        const { x, y, z, pitch, yaw, roll, perspective, originY } = this.fpvCamera;
        const gameContainer = document.getElementById('game-container');
        const gamePage = document.getElementById('game-page');

        // The order is crucial: translate first, then apply rotations.
        gameContainer.style.transform = ` translate3d(${x}px, ${y}px, ${z}px) rotateX(${pitch}deg) rotateY(${yaw}deg) rotateZ(${roll}deg)`;

        gamePage.style.setProperty('--fpv-perspective', `${perspective}px`);
        // Dynamically adjust the perspective origin to match the camera's position. This makes rotation feel like it's happening "in-place".
        gamePage.style.setProperty('--fpv-origin', `50% 50%`);
    }

    /**
     * Toggles control over a specific NPC.
     * @param {number} npcId - The ID of the NPC to control.
     * @param {string} managerType - The type of manager ('human' or 'non-human').
     */
    toggleNpcControl(npcId, managerType) {
        if (this.isNpcControlMode && this.controlledNpcId === npcId) {
            // Stop controlling the current NPC
            this.isNpcControlMode = false;
            this.controlledNpcId = null;
            this.controlledNpcManagerType = null;
            this.ui.displayMessage('NPC control released.');
            this.debugManager.log('Player released control of NPC.');
            this.ui.toggleExitControlModeButton(false);
        } else {
            // Start controlling the new NPC
            this.isNpcControlMode = true;
            this.controlledNpcId = npcId;
            this.controlledNpcManagerType = managerType;
            this.ui.displayMessage('Click on the grid to move the selected NPC.');
            this.ui.toggleExitControlModeButton(true);
            this.debugManager.log(`Player took control of ${managerType} NPC ID: ${npcId}.`);
        }
        // Reset any RTS tool that might be active
        this.gameController.resetToolMode();
    }
}